// src/pages/ProfitLossReport.js
import React, { useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  MenuItem,
} from '@mui/material'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// You can change this to use an env var later
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://157.180.43.56:5050'

// ---- SAFE NUMBER FORMATTER ----
const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const fmt = (v) => num(v).toLocaleString()

// Inclusive day count helper for overlap between selected range and contract
const overlapDaysInclusive = (rangeStart, rangeEnd, contractStart, contractEnd) => {
  if (!rangeStart || !rangeEnd || !contractStart) return 0

  const rStart = new Date(rangeStart)
  const rEnd = new Date(rangeEnd)
  const cStart = new Date(contractStart)
  const cEnd = contractEnd ? new Date(contractEnd) : null

  if (isNaN(rStart) || isNaN(rEnd) || isNaN(cStart)) return 0

  // activeStart = max(contract_start, rangeStart)
  const activeStart = cStart > rStart ? cStart : rStart

  // activeEnd = min(contract_end (if any), rangeEnd) or rangeEnd if open-ended
  let activeEnd = rEnd
  if (cEnd && cEnd < rEnd) {
    activeEnd = cEnd
  }

  const ms = activeEnd.getTime() - activeStart.getTime()
  if (isNaN(ms) || ms < 0) return 0

  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
}

export default function ProfitLossReport() {
  const [vehicles, setVehicles] = useState([])
  const [reportData, setReportData] = useState([])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0])
  const [selectedVehicleId, setSelectedVehicleId] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // ---- Load vehicles from backend (for dropdown & contract info) ----
  useEffect(() => {
    const loadVehicles = async () => {
      setLoading(true)
      setErrorMsg('')
      try {
        const res = await fetch(`${API_BASE_URL}/api/vehicles`)
        if (!res.ok) {
          throw new Error(`Vehicles request failed with status ${res.status}`)
        }
        const data = await res.json()
        // data.vehicles from /api/vehicles
        setVehicles(data.vehicles || [])
      } catch (err) {
        console.error('Error loading vehicles:', err)
        setErrorMsg('Could not load vehicles from backend.')
      } finally {
        setLoading(false)
      }
    }

    loadVehicles()
  }, [])

  // --- report generation using backend /api/reports/profit-loss ---
  const generateReport = async () => {
    if (!fromDate || !toDate) {
      alert('Please select date range')
      return
    }

    setLoading(true)
    setErrorMsg('')
    try {
      const url = new URL('/api/reports/profit-loss', API_BASE_URL)
      url.searchParams.set('startDate', fromDate)
      url.searchParams.set('endDate', toDate)

      const res = await fetch(url.toString())
      if (!res.ok) {
        throw new Error(`Profit-loss request failed with status ${res.status}`)
      }
      const data = await res.json()

      const items = data.items || []

      // Build rows for table, merging with vehicles to get daily_rate & contract info
      const rows = items.map((item) => {
        const vehicle =
          vehicles.find((v) => String(v.id) === String(item.vehicle_id)) || {}

        const dailyRate = num(vehicle.daily_rate)

        // Expected amount only for overlap between:
        // [fromDate..toDate] and [contract_start..contract_end]
        const contractDaysInRange = overlapDaysInclusive(
          fromDate,
          toDate,
          vehicle.contract_start,
          vehicle.contract_end
        )
        const expectedAmount = num(contractDaysInRange * dailyRate)

        return {
          vehicle_id: item.vehicle_id,
          plate_number: item.plate_number || vehicle.plate_number || '',
          daily_rate: dailyRate,
          paid_days: item.paidDays || 0,
          expected_amount: expectedAmount,
          total_paid: item.totalPaid || 0,
          purchase_price: item.purchase_price ?? vehicle.purchase_price ?? 0,
          profit: item.profitLoss ?? 0,
        }
      })

      // Filter by selected vehicle if chosen
      const filteredRows = selectedVehicleId
        ? rows.filter((r) => String(r.vehicle_id) === String(selectedVehicleId))
        : rows

      setReportData(filteredRows)
    } catch (err) {
      console.error('Error generating profit-loss report:', err)
      setErrorMsg('Failed to generate Profit & Loss report.')
      setReportData([])
    } finally {
      setLoading(false)
    }
  }

  // --- exports ---
  const exportToExcel = () => {
    if (reportData.length === 0) return

    const rows = reportData.map((r) => ({
      Vehicle: r.plate_number,
      'Daily Rate': num(r.daily_rate),
      'Paid Days': r.paid_days,
      'Expected (TZS)': num(r.expected_amount),
      'Total Paid (TZS)': num(r.total_paid),
      'Purchase Price (TZS)': r.purchase_price ?? 0,
      'Profit / Loss (TZS)': num(r.profit),
    }))

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Profit & Loss')
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    XLSX.writeFile(
      workbook,
      `profit_loss_${fromDate || 'start'}_${toDate || 'end'}_${stamp}.xlsx`
    )
  }

  const exportToPDF = () => {
    if (reportData.length === 0) return

    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const title = 'Profit & Loss Report'
    const range = `Range: ${fromDate || 'N/A'} — ${toDate || 'N/A'}`
    const stamp = `Generated: ${new Date().toLocaleString()}`

    // Title block
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(20)
    doc.text(title, 48, 60)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    doc.text(range, 48, 84)
    doc.text(stamp, 48, 102)

    autoTable(doc, {
      startY: 128,
      theme: 'grid',
      head: [
        [
          'Vehicle',
          'Daily Rate',
          'Paid Days',
          'Expected (TZS)',
          'Total Paid (TZS)',
          'Purchase Price (TZS)',
          'Profit / Loss (TZS)',
        ],
      ],
      body: reportData.map((r) => [
        r.plate_number,
        fmt(r.daily_rate),
        r.paid_days,
        fmt(r.expected_amount),
        fmt(r.total_paid),
        r.purchase_price != null ? fmt(r.purchase_price) : 'N/A',
        fmt(r.profit),
      ]),
      styles: {
        font: 'helvetica',
        fontSize: 10,
        textColor: [20, 20, 20],
        lineColor: [140, 140, 140],
        lineWidth: 0.6,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
        halign: 'right',
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 120 }, // Vehicle
      },
      headStyles: {
        fillColor: [33, 33, 33],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      foot: [
        [
          'Totals',
          '',
          '',
          fmt(reportData.reduce((a, r) => a + num(r.expected_amount), 0)),
          fmt(reportData.reduce((a, r) => a + num(r.total_paid), 0)),
          fmt(reportData.reduce((a, r) => a + num(r.purchase_price), 0)),
          fmt(reportData.reduce((a, r) => a + num(r.profit), 0)),
        ],
      ],
      footStyles: {
        fillColor: [33, 33, 33],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'right',
      },
      margin: { left: 48, right: 48 },
      tableWidth: 'auto',
      didParseCell: (data) => {
        // Profit/Loss column coloring (red/green)
        if (data.section === 'body' && data.column.index === 6) {
          const numeric = Number(
            String(data.cell.raw ?? '0').replace(/[^0-9.-]/g, '')
          )
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.textColor = numeric >= 0 ? [0, 128, 0] : [200, 0, 0]
        }
      },
    })

    doc.save(`profit_loss_${fromDate || 'start'}_${toDate || 'end'}.pdf`)
  }

  // totals (for on-screen footer)
  const totals = reportData.reduce(
    (acc, r) => {
      acc.totalExpected += num(r.expected_amount)
      acc.totalPaid += num(r.total_paid)
      acc.totalPurchase += num(r.purchase_price)
      acc.totalProfit += num(r.profit)
      return acc
    },
    { totalExpected: 0, totalPaid: 0, totalPurchase: 0, totalProfit: 0 }
  )

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        📈 Profit & Loss Report
      </Typography>

      {errorMsg && (
        <Typography sx={{ mb: 2 }} color="warning.main">
          {errorMsg}
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
        >
          {/* Vehicle selector */}
          <TextField
            select
            label="Vehicle"
            value={selectedVehicleId}
            onChange={(e) => setSelectedVehicleId(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All Vehicles</MenuItem>
            {vehicles.map((v) => (
              <MenuItem key={v.id} value={String(v.id)}>
                {v.plate_number}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Box flexGrow={1} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={generateReport}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Generate'}
            </Button>
            <Button
              variant="outlined"
              onClick={exportToExcel}
              disabled={reportData.length === 0}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              onClick={exportToPDF}
              disabled={reportData.length === 0}
            >
              Export PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {vehicles.length === 0 && !errorMsg && (
        <Typography sx={{ mb: 2 }} color="warning.main">
          No vehicles available from backend.
        </Typography>
      )}

      {reportData.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                <TableCell>Vehicle</TableCell>
                <TableCell align="right">Daily Rate</TableCell>
                <TableCell align="right">Paid Days</TableCell>
                <TableCell align="right">Expected (TZS)</TableCell>
                <TableCell align="right">Total Paid (TZS)</TableCell>
                <TableCell align="right">Purchase Price (TZS)</TableCell>
                <TableCell align="right">Profit / Loss (TZS)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.plate_number}</TableCell>
                  <TableCell align="right">{fmt(r.daily_rate)}</TableCell>
                  <TableCell align="right">{r.paid_days}</TableCell>
                  <TableCell align="right">{fmt(r.expected_amount)}</TableCell>
                  <TableCell align="right">{fmt(r.total_paid)}</TableCell>
                  <TableCell align="right">
                    {r.purchase_price != null
                      ? fmt(r.purchase_price)
                      : 'N/A'}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: num(r.profit) >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {fmt(r.profit)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Totals</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(totals.totalExpected)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(totals.totalPaid)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(totals.totalPurchase)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 700,
                    color:
                      num(totals.totalProfit) >= 0
                        ? 'success.main'
                        : 'error.main',
                  }}
                >
                  {fmt(totals.totalProfit)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        vehicles.length > 0 && (
          <Typography color="text.secondary">
            Pick a date range and click Generate.
          </Typography>
        )
      )}
    </Box>
  )
}